import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { CheckCircle, XCircle, Clock, Users } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function RegistrarAsistenciaPage() {
  const { cursoId, jornadaId } = useParams();
  const [curso, setCurso] = useState(null);
  const [jornada, setJornada] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  
  const [formData, setFormData] = useState({
    email: '',
    dni: ''
  });

  useEffect(() => {
    fetchCursoInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursoId, jornadaId]);

  const fetchCursoInfo = async () => {
    try {
      const url = jornadaId
        ? `${API_URL}/capacitaciones/curso/${cursoId}/asistencia-info?jornada_id=${jornadaId}`
        : `${API_URL}/capacitaciones/curso/${cursoId}/asistencia-info`;
      const response = await axios.get(url);
      setCurso(response.data);
      if (response.data.jornada) setJornada(response.data.jornada);
    } catch (err) {
      if (err.response?.status === 403) {
        setError(jornadaId
          ? 'El link de asistencia de esta jornada no está habilitado'
          : 'El registro de asistencia no está habilitado para este curso');
      } else if (err.response?.status === 404) {
        setError(err.response?.data?.detail || 'No encontrado');
      } else {
        setError('Error al cargar la información del curso');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email.trim() || !formData.dni.trim()) {
      setError('Completá email y DNI');
      return;
    }
    
    // Validar formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('El formato del email es inválido');
      return;
    }
    
    setSubmitting(true);
    setError(null);
    
    try {
      const url = jornadaId
        ? `${API_URL}/capacitaciones/curso/${cursoId}/registrar-asistencia?jornada_id=${jornadaId}`
        : `${API_URL}/capacitaciones/curso/${cursoId}/registrar-asistencia`;
      const response = await axios.post(url, {
        email: formData.email.trim().toLowerCase(),
        dni: formData.dni.trim()
      });
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al registrar asistencia');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('T')[0].split('-');
    const date = new Date(parts[0], parts[1] - 1, parts[2]);
    return date.toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  // Resultado de asistencia registrada
  if (result) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white py-12 px-4">
        <div className="max-w-md mx-auto">
          <Card className={result.ya_registrado ? "border-amber-300" : "border-emerald-300"}>
            <CardContent className="pt-8 pb-8 text-center">
              {result.ya_registrado ? (
                <Clock className="w-20 h-20 text-amber-500 mx-auto mb-4" />
              ) : (
                <CheckCircle className="w-20 h-20 text-emerald-500 mx-auto mb-4" />
              )}
              
              <h2 className={`text-2xl font-bold mb-2 ${result.ya_registrado ? 'text-amber-700' : 'text-emerald-700'}`}>
                {result.ya_registrado ? '¡Ya estás registrado/a!' : '¡Presente registrado!'}
              </h2>
              
              <p className="text-lg text-gray-700 mb-4">
                {result.nombre}
              </p>
              
              <p className="text-gray-600">
                {result.message}
              </p>
              
              {curso && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Curso:</p>
                  <p className="font-medium">{curso.nombre}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Error de curso no habilitado
  if (error && !curso) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-red-50 to-white py-12 px-4">
        <div className="max-w-md mx-auto">
          <Card className="border-red-300">
            <CardContent className="pt-8 pb-8 text-center">
              <XCircle className="w-20 h-20 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-red-700 mb-2">No disponible</h2>
              <p className="text-gray-600">{error}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white py-12 px-4">
      <div className="max-w-md mx-auto">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="/logo-colegio.png" 
            alt="Logo CATSLZ" 
            className="h-24 mx-auto mb-4"
          />
        </div>

        <Card>
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-emerald-600" />
            </div>
            <CardTitle className="text-xl">Registro de Asistencia</CardTitle>
          </CardHeader>
          
          <CardContent>
            {/* Info del curso */}
            {curso && (
              <div className="mb-6 p-4 bg-emerald-50 rounded-lg">
                <h3 className="font-semibold text-emerald-800 mb-2">{curso.nombre}</h3>
                <div className="text-sm text-emerald-700 space-y-1">
                  {jornada ? (
                    <p className="font-medium" data-testid="jornada-label">
                      {jornada.etiqueta} · {formatDate(jornada.fecha)}
                    </p>
                  ) : (
                    <>
                      {curso.fechas?.length > 0 && (
                        <p>{curso.fechas.map(f => formatDate(f)).join(' | ')}</p>
                      )}
                      {curso.hora && <p>Horario: {curso.hora} hs</p>}
                    </>
                  )}
                  {curso.modalidad && <p>Modalidad: {curso.modalidad}</p>}
                </div>
              </div>
            )}

            {error && (
              <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="asistencia-instruccion">
              <p className="text-sm font-semibold text-emerald-800">
                Ingresá el email y DNI con los que te inscribiste al curso
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="Ej: tucorreo@email.com"
                  className="mt-1"
                  data-testid="asistencia-email"
                />
              </div>

              <div>
                <Label htmlFor="dni">DNI (sin puntos) *</Label>
                <Input
                  id="dni"
                  type="text"
                  value={formData.dni}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setFormData(prev => ({ ...prev, dni: value }));
                  }}
                  placeholder="Ej: 30123456"
                  className="mt-1"
                  inputMode="numeric"
                  data-testid="asistencia-dni"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                disabled={submitting}
                data-testid="asistencia-submit"
              >
                {submitting ? 'Registrando...' : 'Registrar Asistencia'}
              </Button>
            </form>

            <p className="mt-4 text-xs text-center text-gray-500" data-testid="asistencia-soporte">
              Si surge algún error o dificultad en el registro de la asistencia, comunicate a{' '}
              <a href="mailto:info@colegiotslomas.org" className="text-emerald-700 font-medium underline">
                info@colegiotslomas.org
              </a>{' '}
              indicando tus datos.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
